/**
 * cart-wrapping-note.js
 *
 * PDP で選択された表示 line item property `ラッピング`（なし / まとめてラッピング / 個別ラッピング）を
 * カート全体で**重複排除した種別サマリ**（3 パターン: 個別ラッピング / まとめてラッピング /
 * 個別ラッピング/まとめてラッピング）にして order.note のマーカー領域へ 1 行で書き込む NE ブリッジ。
 * ネクストエンジンが `〔ラッピング指定〕__文字__〔/ラッピング指定〕` の中身を「項目に変換 →
 * ピッキング指示への反映」で拾う前提。どの商品をどう包むかは商品オプション（line item property）に
 * 残るので、note には種別サマリのみ載せる。
 *
 * 設計:
 * - 真実の保管場所は各 line item の `ラッピング` property。`_` を付けないためカート／チェックアウト／
 *   admin に顧客可読で表示され、Cart AJAX API (/cart.js) からも読める。当モジュールは cart 状態 →
 *   order.note へミラーするだけで、購入経路は一切ブロックしない（best-effort）。
 *   顧客に見せないのは order.note（＝NE 用「備考欄」）のみ。
 * - note は「マーカー領域だけ」を差し替えるため、顧客の自由記述（将来 settings.show_cart_note を
 *   有効化した場合）と共存できる。マーカー外の文字列は保持する。
 *
 * 同期トリガー:
 * - `PUB_SUB_EVENTS.cartUpdate`（追加 / 削除 / 数量変更）… 当セッションの操作を都度反映
 * - カートページ表示時（`#main-cart-footer` 存在時）の初回リコンサイル … 永続カート（別セッションで
 *   追加した商品が残っているケース等）を checkout 前に最新化
 *
 * マウント: <cart-wrapping-note>（cart-drawer / main-cart-footer から render）。両方に存在しても
 * singleton ガードで実働は 1 インスタンスのみ。
 *
 * ⚠️ 既知のエッジ: 別セッションで積んだ永続カートを「カートページを経由せず」ドロワーから
 * エクスプレス決済した場合、cartUpdate が発火しないため note が未同期になり得る。カートページ
 * 経由の決済・当セッションでの追加は網羅する。NE 側はマーカー無し（＝ラッピング指定なし）を
 * 既定として扱うこと。
 */
(function () {
  'use strict';

  var PROP_KEY = 'ラッピング';
  var NONE_VALUE = 'なし';
  var MARKER_START = '〔ラッピング指定〕';
  var MARKER_END = '〔/ラッピング指定〕';
  // ピッキング指示に載せる種別サマリの正規順（個別 → まとめて）。
  var TYPE_ORDER = ['個別ラッピング', 'まとめてラッピング'];

  // 既存のマーカー領域（前後の空白・改行を含む）を除去する正規表現。
  // 〔 〕（隅付き括弧）は顧客が入力しにくいため、自由記述との衝突を避けられる。
  var BLOCK_RE = new RegExp('[\\r\\n]*' + MARKER_START + '[\\s\\S]*?' + MARKER_END + '[\\r\\n]*', 'g');

  /**
   * 全 line item のラッピング種別を**重複排除**し、正規順（個別 → まとめて）で `/` 連結した
   * サマリを 1 行のマーカー付きブロックにして返す。ネクストエンジンが
   * `〔ラッピング指定〕__文字__〔/ラッピング指定〕` で中身を拾い**ピッキング指示**へ反映する前提。
   * どの商品をどう包むかは商品オプション（line item property `ラッピング`）に残るので、
   * ここでは種別サマリのみ（3 パターン）。対象が 0 件なら空文字。
   *
   * 出力例:
   *   〔ラッピング指定〕個別ラッピング〔/ラッピング指定〕
   *   〔ラッピング指定〕まとめてラッピング〔/ラッピング指定〕
   *   〔ラッピング指定〕個別ラッピング/まとめてラッピング〔/ラッピング指定〕
   * @param {Array} items - /cart.js の items 配列
   * @returns {string}
   */
  function buildWrappingBlock(items) {
    if (!Array.isArray(items)) return '';
    var present = new Set(); // Set: __proto__ 等の特殊値でもプロトタイプ汚染しない
    for (var i = 0; i < items.length; i++) {
      var item = items[i] || {};
      var props = item.properties || {};
      var value = props[PROP_KEY];
      if (value == null) continue;
      // サニタイズ: 改行を除去して 1 行を維持し、区切り文字「/」を全角「／」に置換する。
      //（改ざんされた / 想定外の property 値が単一行フォーマットや NE のピッキング指示反映を壊さないように）
      value = String(value).trim().replace(/[\r\n]+/g, '').replace(/\//g, '／');
      if (!value || value === NONE_VALUE) continue;
      present.add(value);
    }
    // 正規順（個別 → まとめて）で並べ、想定外の値があれば末尾に取りこぼさず追加。
    var ordered = [];
    for (var t = 0; t < TYPE_ORDER.length; t++) {
      if (present.has(TYPE_ORDER[t])) {
        ordered.push(TYPE_ORDER[t]);
        present.delete(TYPE_ORDER[t]);
      }
    }
    present.forEach(function (val) {
      ordered.push(val);
    });
    if (ordered.length === 0) return '';
    return MARKER_START + ordered.join('/') + MARKER_END;
  }

  /**
   * 既存 note からマーカー領域を除去し、block があれば末尾に付与する。
   * マーカー外（顧客自由記述）は保持。冪等: mergeNote(block, block) === block。
   * @param {string} currentNote
   * @param {string} block - buildWrappingBlock の戻り値（空可）
   * @returns {string}
   */
  function mergeNote(currentNote, block) {
    var base = (currentNote || '').replace(BLOCK_RE, '\n').trim();
    if (!block) return base;
    return base ? base + '\n\n' + block : block;
  }

  // singleton: drawer と footer 両方に mount されても実働 1 つ。
  // Section Rendering の HTML 差し替えでも、`disconnectedCallback` で残存インスタンスを
  // **同期的に**ハンドオフすることで、常にどれか 1 つが cartUpdate を購読している状態を保つ
  //（setTimeout の隙間で cartUpdate を取りこぼさない・二重購読もしない）。
  var activeInstance = null;

  class CartWrappingNote extends HTMLElement {
    connectedCallback() {
      this.activate();
    }

    disconnectedCallback() {
      if (activeInstance !== this) return; // passive インスタンスは何もしない
      activeInstance = null;
      this._owner = false;
      if (this._unsub) {
        this._unsub();
        this._unsub = null;
      }
      // DOM に別インスタンスが残っていれば同期的にアクティブ化（購読の空白を作らない）。
      var next = document.querySelector('cart-wrapping-note');
      if (next && next !== this) next.activate();
    }

    activate() {
      if (activeInstance && activeInstance !== this && activeInstance.isConnected) return; // 既に別が担当中
      activeInstance = this;
      this._owner = true;

      if (!this._sync) {
        this._sync =
          typeof debounce === 'function' ? debounce(this.sync.bind(this), 300) : this.sync.bind(this);
      }
      if (!this._unsub) {
        this._unsub =
          typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
            ? subscribe(PUB_SUB_EVENTS.cartUpdate, this._sync)
            : null;
      }

      // カートページでは初回リコンサイル（永続カートの note を checkout 前に最新化）。
      // 他ページは cartUpdate 駆動のみ（毎ページの /cart.js を避ける）。
      if (document.getElementById('main-cart-footer')) {
        this._sync();
      }
    }

    async sync() {
      // 連続 cartUpdate での並行実行を直列化（古いカート状態の note で上書きするのを防ぐ）。
      if (this._syncing) {
        this._needsSyncAgain = true;
        return;
      }
      this._syncing = true;
      this._needsSyncAgain = false;

      var routes = window.routes || {};
      var cartUrl = routes.cart_url || '/cart';
      try {
        var res = await fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        var cart = await res.json();
        var block = buildWrappingBlock(cart.items);

        // 画面に note 入力欄があれば、その最新値（未保存の自由記述を含む）を優先してマージ基準にする。
        // /cart.js の cart.note は最後に保存された値。drawer と footer の 2 つが同時に存在し得るため、
        // フォーカス中（＝ユーザーが実際に入力している）textarea を優先し、無ければ先頭を使う。
        var noteFields = document.querySelectorAll('textarea[name="note"]');
        var currentNote = cart.note;
        if (noteFields.length > 0) {
          var activeField =
            Array.from(noteFields).find(function (t) {
              return t === document.activeElement;
            }) || noteFields[0];
          currentNote = activeField.value;
        }
        var next = mergeNote(currentNote, block);
        if (next === (currentNote || '')) return; // 変化なし → 書き込み不要（冪等）

        // 画面上の全 note 入力欄の value も揃える（将来 settings.show_cart_note 有効化時の見た目整合）。
        // イベントは意図的に発火しない: Dawn 標準の cart-note (assets/cart.js) は `input`
        //（＝ユーザーの実入力）でのみ /cart/update.js を呼ぶ。programmatic な value 代入や change
        // では発火しないため、下の自前 fetch を唯一の書き込み経路にすることで二重送信/競合を避ける。
        noteFields.forEach(function (t) {
          // フォーカス中の入力欄は選択範囲を退避・復元し、value 代入でカーソルが末尾へ飛ぶのを防ぐ。
          if (t === document.activeElement) {
            var start = t.selectionStart;
            var end = t.selectionEnd;
            t.value = next;
            t.setSelectionRange(start, end);
          } else {
            t.value = next;
          }
        });

        var updateUrl = routes.cart_update_url || '/cart/update.js';
        var config =
          typeof fetchConfig === 'function'
            ? fetchConfig()
            : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
        var updateRes = await fetch(
          updateUrl,
          Object.assign({}, config, { body: JSON.stringify({ note: next }) })
        );
        if (!updateRes || !updateRes.ok) {
          console.warn('[cart-wrapping-note] update failed:', updateRes && updateRes.status);
        }
      } catch (e) {
        // note 同期失敗は購入をブロックしない（best-effort）。
        console.warn('[cart-wrapping-note] sync failed:', e);
      } finally {
        this._syncing = false;
        if (this._needsSyncAgain) {
          this._needsSyncAgain = false;
          this.sync();
        }
      }
    }
  }

  // テスト用に純粋関数・定数を静的公開（black-box eval → static 参照）。
  CartWrappingNote.buildWrappingBlock = buildWrappingBlock;
  CartWrappingNote.mergeNote = mergeNote;
  CartWrappingNote.MARKER_START = MARKER_START;
  CartWrappingNote.MARKER_END = MARKER_END;

  if (window.customElements && !customElements.get('cart-wrapping-note')) {
    customElements.define('cart-wrapping-note', CartWrappingNote);
  }
})();
